import {
  FunctionComponent,
  useCallback,
} from 'react';
import { Document } from '@prismicio/client/types/documents';
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import { Link } from 'prismic-reactjs';
import { prismicLinkResolver } from '../utils/common';
import { getColor } from '../utils/colors';
import LinkWithQuery from './LinkWithQuery';

export const groupByBusinessUnit = (products, bu) => {
  const productsAssociated = products.filter((p) => p.businessUnit === bu.businessUnitKey);

  return productsAssociated;
};

export const getPrismicLink = (link, data) => {
  if (link?.type === 'clusterpage') {
    const verticalData = data.clusters?.find((cluster) => cluster.id === link.id);
    const newLink = {
      ...link,
      data: verticalData?.data,
    };
    return Link.url(newLink, prismicLinkResolver);
  }
  return Link.url(link, prismicLinkResolver);
};

const getBusinessUnitsWithProducts = (products, businessUnits) => {
  const buWithProducts = [...new Set(products.flatMap((p) => p.businessUnit))];
  return businessUnits.filter(({ businessUnitKey }) => buWithProducts.includes(businessUnitKey));
};

const getBusinessUnitsWithoutProducts = (products, businessUnits) => {
  const buWithProducts = [...new Set(products.flatMap((p) => p.businessUnit))];
  return businessUnits.filter(({ businessUnitKey }) => !buWithProducts.includes(businessUnitKey));
};

interface Props {
  data: Document['data'];
}

const HeaderBU: FunctionComponent<Props> = ({ data }) => {
  const businessUnitsMenu = getBusinessUnitsWithProducts(data.productsByBu, data.businessUnit);
  const businessUnitsLinks = getBusinessUnitsWithoutProducts(data.productsByBu, data.businessUnit);
  const getColumnClass = useCallback(() => {
    let cols = businessUnitsMenu.length;

    if (businessUnitsLinks.length) cols += 1;

    switch (cols) {
      case 1:
        return 'md:grid-cols-1';
      case 2:
        return 'md:grid-cols-2';
      case 3:
        return 'md:grid-cols-3';
      case 4:
        return 'md:grid-cols-4';
      case 5:
        return 'md:grid-cols-5';
      case 6:
        return 'md:grid-cols-6';
      default:
        return 'md:grid-cols-1';
    }
  }, [businessUnitsLinks, businessUnitsMenu]);

  return (
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-6 py-10 lg:px-8">
      <nav className={classNames('grid gap-y-6 gap-x-6 md:gap-x-8 md:gap-y-0', getColumnClass())}>
        {!!businessUnitsLinks.length && (
          <div>
            <div className="flow-root">
              <ul className="-my-2">
                {businessUnitsLinks.map((item) => {
                  const verticalColor = item.color ? getColor(item.color, 600) : null;

                  return (
                    <li key={item.name}>
                      <LinkWithQuery
                        key={nanoid()}
                        href={getPrismicLink(item.businessUnitLink, data)}
                        target={item.businessUnitLink?.target ?? '_self'}
                        className="flex gap-x-4 py-2 text-sm font-semibold leading-6"
                      >
                        {
                          verticalColor
                          && <span className={classNames('block w-1 rounded-full flex-none', verticalColor.bg)} />
                        }
                        <span>
                          {item.name}
                          <small className="block text-xxs leading-3 text-gray-600">{item.description}</small>
                        </span>
                      </LinkWithQuery>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
        {
          businessUnitsMenu.map((bu) => {
            const productsAssociated = groupByBusinessUnit(data.productsByBu, bu);
            const verticalColor = bu.color ? getColor(bu.color, 600) : null;

            return (
              <div key={bu.name}>
                <div className="flow-root">
                  <h3 className="flex gap-x-4 text-sm font-medium leading-6 text-gray-500">
                    {
                      verticalColor
                      && <span className={classNames('block w-1 rounded-full flex-none', verticalColor.bg)} />
                    }
                    <span>
                      {bu.name}
                      <small className="block text-xxs leading-3 text-gray-600">{bu.description}</small>
                    </span>
                  </h3>
                  <div className="mt-6 flow-root">
                    <div className="-my-2 flex flex-col">
                      {productsAssociated.map((item) => (
                        <LinkWithQuery
                          key={nanoid()}
                          href={getPrismicLink(item.link, data)}
                          className="gap-x-4 py-2 text-sm font-semibold leading-6"
                        >
                          {item.title}
                        </LinkWithQuery>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        }
      </nav>
    </div>
  );
};
export default HeaderBU;
